import { adminApiClient } from './adminApiClient';
import { createCrudService } from './createCrudService';
import type { WorkflowDefinition, WorkflowStep } from './workflowDefinitionsService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WorkflowExecutionStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'waiting_input'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type WorkflowStepExecutionStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'waiting_input'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface WorkflowStepExecution {
  id: string;
  workflow_execution_id: string;
  workflow_step_id: string;
  status: WorkflowStepExecutionStatus;
  input_data: Record<string, unknown> | null;
  output_data: Record<string, unknown> | null;
  error_message: string | null;
  error_code: string | null;
  attempt: number;
  duration_ms: number | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  step?: WorkflowStep;
}

export interface WorkflowExecution {
  id: string;
  tenant_id: number;
  workflow_definition_id: string;
  user_id: number | null;
  status: WorkflowExecutionStatus;
  current_step_id: string | null;
  context_data: Record<string, unknown> | null;
  error_message: string | null;
  error_code: string | null;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  definition?: WorkflowDefinition;
  current_step?: WorkflowStep;
  step_executions?: WorkflowStepExecution[];
  step_executions_count?: number;
}

export interface StoreWorkflowExecutionRequest {
  workflow_definition_id: string;
  context_data?: Record<string, unknown>;
}

export interface UpdateWorkflowExecutionRequest {
  context_data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const basePath = '/admin/workflows/executions';

const crudService = createCrudService<
  WorkflowExecution,
  StoreWorkflowExecutionRequest,
  UpdateWorkflowExecutionRequest
>(basePath);

export const workflowExecutionsService = {
  ...crudService,

  async cancel(id: string): Promise<{ data: WorkflowExecution }> {
    const response = await adminApiClient.post<{ data: WorkflowExecution }>(
      `${basePath}/${id}/cancel`,
    );
    return response.data;
  },

  async resume(
    id: string,
    input?: Record<string, unknown>,
  ): Promise<{ data: WorkflowExecution }> {
    const response = await adminApiClient.post<{ data: WorkflowExecution }>(
      `${basePath}/${id}/resume`,
      input ? { input } : undefined,
    );
    return response.data;
  },

  async listWithFilters(params?: {
    status?: string;
    workflow_definition_id?: string;
    user_id?: number;
    page?: number;
  }): Promise<{ data: WorkflowExecution[] }> {
    const response = await adminApiClient.get<{ data: WorkflowExecution[] }>(
      basePath,
      { params },
    );
    return response.data;
  },
};
